import { CertifiedMetricsParams } from "./table.types";
import { DATE_PATH_FORMAT } from "portal/utils/dates";
import { DateRange } from "@mui/x-date-pickers-pro";
import { DateTime } from "luxon";
import {
  JobMetricResponse,
  JobMetricsByJobsByRobotResponse,
} from "protos/portal/metrics";
import { Metric } from "portal/utils/metrics";
import { portalApi } from "portal/state/portalApi";
import { ReportMode } from "protos/portal/reports";
import { RobotResponse } from "protos/portal/robots";
import { toCertifiedFields } from "./helpers";
import { useEffect, useMemo, useState } from "react";
import { useMutationPopups } from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";

/**
 * Loads certified job metrics for the report table when mode is "jobs" and
 * robots and a date range are available.
 */
export const useReportTableRobotJobs = ({
  dateRange,
  mode,
  selectedRobots,
  useJigsaw,
  visibleMetrics,
}: {
  dateRange: DateRange<DateTime>;
  mode: ReportMode;
  selectedRobots: RobotResponse[];
  useJigsaw: boolean;
  visibleMetrics: Metric[];
}): {
  fetchJobMetricsBody?: CertifiedMetricsParams;
  isJobsDataLoading: boolean;
  jobMetricsError: boolean;
  jobsBySerial: Record<string, JobMetricResponse[]>;
} => {
  const { i18n } = useTranslation();

  const [rangeStart, rangeEnd] = dateRange;
  const [getJobCertifiedMetrics] = useMutationPopups(
    portalApi.endpoints.getJobCertifiedMetrics.useMutation()
  );
  const [jobMetrics, setJobMetrics] =
    useState<JobMetricsByJobsByRobotResponse>();
  const [isJobsDataLoading, setIsJobsDataLoading] = useState(false);
  const [jobMetricsError, setJobMetricsError] = useState(false);

  const fetchJobMetricsBody = useMemo(():
    | CertifiedMetricsParams
    | undefined => {
    if (
      mode === ReportMode.REPORT_MODE_JOBS &&
      selectedRobots.length > 0 &&
      rangeStart &&
      rangeEnd &&
      visibleMetrics.length > 0
    ) {
      return {
        serials: selectedRobots.map((robot) => robot.serial),
        startDate: rangeStart.toFormat(DATE_PATH_FORMAT),
        endDate: rangeEnd.toFormat(DATE_PATH_FORMAT),
        fields: toCertifiedFields(visibleMetrics),
        useJigsaw,
      };
    }
  }, [mode, rangeEnd, rangeStart, selectedRobots, useJigsaw, visibleMetrics]);

  useEffect(() => {
    if (!fetchJobMetricsBody) {
      setJobMetrics(undefined);
      setJobMetricsError(false);
      setIsJobsDataLoading(false);
      return;
    }
    const fetchJobMetrics = async (
      body: CertifiedMetricsParams
    ): Promise<void> => {
      setIsJobsDataLoading(true);
      const { data, error } = await getJobCertifiedMetrics({
        ...body,
        language: i18n.language,
      });
      if (error) {
        setJobMetricsError(true);
      }
      if (data) {
        setJobMetrics(data);
      }
      setIsJobsDataLoading(false);
    };

    fetchJobMetrics(fetchJobMetricsBody);
  }, [getJobCertifiedMetrics, fetchJobMetricsBody, i18n.language]);

  const jobsBySerial = useMemo<Record<string, JobMetricResponse[]>>(() => {
    const bySerial: Record<string, JobMetricResponse[]> = {};
    for (const [serial, jobsByJobId] of Object.entries(
      jobMetrics?.metrics ?? {}
    )) {
      bySerial[serial] = Object.values(jobsByJobId.metrics);
    }
    return bySerial;
  }, [jobMetrics]);

  return {
    fetchJobMetricsBody,
    isJobsDataLoading,
    jobMetricsError,
    jobsBySerial,
  };
};
